import React, { useState, useEffect } from 'react';
import AWSMetrics from './AWSMetrics';
import GoogleAnalyticsEmbed from './GoogleAnalyticsEmbed';
import CloudflareMetrics from './CloudflareMetrics';
import externalAnalyticsService from '../../../services/ExternalAnalyticsService';
import { LoadingSpinner } from '../../UI';
import { FaAws, FaCloudflare, FaGoogle, FaChartBar, FaSync } from 'react-icons/fa';
import '../../../styles/ExternalAnalytics.css';

const ExternalAnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching external analytics data...');
        
        // Only fetch AWS and Cloudflare data since Google Analytics will use the embed API
        const analyticsData = await externalAnalyticsService.getAllExternalAnalytics();
        console.log('Received analytics data:', analyticsData);
        
        setData(analyticsData);
        
        // Check for errors in each service
        const newErrors = {};
        if (analyticsData.aws?.error) newErrors.aws = analyticsData.aws.error;
        if (analyticsData.cloudflare?.error) newErrors.cloudflare = analyticsData.cloudflare.error;
        
        setErrors(newErrors);
      } catch (err) {
        console.error('Failed to fetch external analytics:', err);
        setErrors({ general: err.message });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Set up polling for real-time updates
    const pollingInterval = setInterval(() => {
      setRefreshKey(prevKey => prevKey + 1);
    }, 5 * 60 * 1000); // Refresh every 5 minutes
    
    return () => clearInterval(pollingInterval);
  }, [refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="external-analytics-dashboard">
      <div className="analytics-header">
        <h2>External Analytics Dashboard</h2>
        <button 
          className="refresh-button" 
          onClick={handleRefresh}
          disabled={loading}
        >
          <FaSync className={loading ? 'spinning' : ''} />
          Refresh All
        </button>
      </div>
      
      {errors.general && (
        <div className="error-banner">
          <p>Error: {errors.general}</p>
        </div>
      )}
      
      <div className="analytics-tabs">
        <button 
          className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => handleTabChange('all')}
        >
          <FaChartBar />
          All Services
        </button>
        <button 
          className={`tab-button ${activeTab === 'aws' ? 'active' : ''}`}
          onClick={() => handleTabChange('aws')}
        >
          <FaAws />
          AWS
        </button>
        <button 
          className={`tab-button ${activeTab === 'ga' ? 'active' : ''}`}
          onClick={() => handleTabChange('ga')}
        >
          <FaGoogle />
          Google Analytics
        </button>
        <button 
          className={`tab-button ${activeTab === 'cloudflare' ? 'active' : ''}`}
          onClick={() => handleTabChange('cloudflare')}
        >
          <FaCloudflare />
          Cloudflare
        </button>
      </div>
      
      {(activeTab === 'all' || activeTab === 'aws') && (
        <div className="analytics-section">
          <div className="section-header">
            <div className="section-title">
              <FaAws />
              <h2>AWS Metrics</h2>
            </div>
          </div>
          {loading && activeTab === 'aws' ? (
            <LoadingSpinner message="Loading AWS metrics..." />
          ) : errors.aws ? (
            <div className="section-error">
              <p>Error loading AWS metrics: {errors.aws}</p>
            </div>
          ) : (
            <AWSMetrics data={data?.aws} />
          )}
        </div>
      )}
      
      {(activeTab === 'all' || activeTab === 'ga') && (
        <GoogleAnalyticsEmbed />
      )}
      
      {(activeTab === 'all' || activeTab === 'cloudflare') && (
        <div className="analytics-section">
          <div className="section-header">
            <div className="section-title">
              <FaCloudflare />
              <h2>Cloudflare</h2>
            </div>
          </div>
          {loading && activeTab === 'cloudflare' ? (
            <LoadingSpinner message="Loading Cloudflare data..." />
          ) : errors.cloudflare ? (
            <div className="section-error">
              <p>Error loading Cloudflare data: {errors.cloudflare}</p>
            </div>
          ) : (
            <CloudflareMetrics data={data?.cloudflare} />
          )}
        </div>
      )}
    </div>
  );
};

export default ExternalAnalyticsDashboard; 